<template>
	<div>
		<div class="sidebar" :class="[colourMode, { open, collapsed }]" :style="{ top: safeTop + 'px' }">
			<div class="sidebar-header">
				<img :src="require('@/assets/mg.png')" alt="MG" class="sidebar-logo" />
				<span class="sidebar-title">Money Guided</span>
				<button class="collapse-btn" @click="$emit('toggle-collapse')">
					<v-icon size="18">{{ collapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
				</button>
			</div>
			<div v-if="navItems.length" class="sidebar-section sidebar-nav">
				<button class="sidebar-section-title" @click="navCollapsed = !navCollapsed">
					<span>Navigate</span>
					<v-icon size="14">{{ navCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
				</button>
				<transition name="section-collapse">
				<div v-show="!navCollapsed" class="section-content">
				<button
					v-for="item in navItems"
					:key="item.route"
					class="sidebar-item"
					:class="{ active: isActiveRoute(item.route) }"
					@click="navigateTo(item.route)"
				>
					<v-icon size="20">{{ item.icon }}</v-icon>
					<span>{{ item.label }}</span>
				</button>
				</div>
			</transition>
			</div>
			<div v-if="pageItemsTitle || actionButton || pageItems.length" class="sidebar-section">
				<button v-if="pageItemsTitle" class="sidebar-section-title" @click="pageCollapsed = !pageCollapsed">
					<span>{{ pageItemsTitle }}</span>
					<v-icon size="14">{{ pageCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
				</button>
				<transition name="section-collapse">
				<div v-show="!pageCollapsed" class="section-content">
				<button v-if="actionButton" class="sidebar-item new-chat" @click="$emit('action-click')">
					<v-icon size="20">{{ actionButton.icon }}</v-icon>
					<span>{{ actionButton.label }}</span>
				</button>
				<div
					v-for="item in pageItems"
					:key="item.id"
					class="chat-item-wrapper"
					:class="{ 'active': item.id === currentPageItemId, 'rich-item': item.subtitle, 'divider-item': item.isDivider }"
					@click="!item.isDivider && $emit('page-item-click', item.id)"
				>
					<div class="chat-item-content">
						<div class="item-title">{{ item.title }}</div>
						<div v-if="item.subtitle" class="item-subtitle">{{ item.subtitle }}</div>
						<div v-if="item.progress !== undefined" class="item-progress">
							<div class="progress-bar">
								<div class="progress-fill" :style="{ width: item.progress + '%', background: item.colour }"></div>
							</div>
							<span class="progress-text" :style="{ color: item.colour }">{{ item.progress }}%</span>
						</div>
						<div v-if="item.meta" class="item-meta">
							<span>{{ item.meta }}</span>
							<v-chip v-if="item.statusChip" size="x-small" :color="item.statusChip.color" :prepend-icon="item.statusChip.icon" :variant="item.statusChip.variant || 'flat'">{{ item.statusChip.label }}</v-chip>
						</div>
					</div>
					<v-menu v-if="!item.subtitle" location="bottom end">
						<template v-slot:activator="{ props }">
							<button class="chat-menu-btn" v-bind="props" @click.stop>
								<v-icon size="16">mdi-dots-horizontal</v-icon>
							</button>
						</template>
						<v-list class="chat-menu-list">
							<v-list-item @click="openRenameDialog(item)">
								<template v-slot:prepend>
									<v-icon size="18">mdi-pencil-outline</v-icon>
								</template>
								<v-list-item-title>Rename</v-list-item-title>
							</v-list-item>
							<v-list-item @click="$emit('page-item-delete', item.id)" class="delete-item">
								<template v-slot:prepend>
									<v-icon size="18" color="#e57373">mdi-delete-outline</v-icon>
								</template>
								<v-list-item-title class="delete-text">Delete</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>
				<div v-if="pageItems.length === 0 && pageItemsEmptyText" class="sidebar-empty">
					{{ pageItemsEmptyText }}
				</div>
				</div>
			</transition>
			</div>

			<!-- Spacer to push footer down when page section is hidden -->
			<div v-if="!pageItemsTitle && !actionButton && !pageItems.length" class="sidebar-spacer"></div>

			<!-- Account section at bottom -->
			<div class="sidebar-footer">
				<v-menu location="top start" :offset="[0, 8]">
					<template v-slot:activator="{ props }">
						<button class="account-btn" v-bind="props">
							<div class="account-avatar">{{ userInitial }}</div>
							<div class="account-info">
								<span class="account-name">{{ userName }}</span>
							</div>
							<v-icon size="16" class="account-chevron">mdi-chevron-up</v-icon>
						</button>
					</template>
					<v-list class="account-menu-list">
						<v-list-item @click="openSettings">
							<template v-slot:prepend>
								<v-icon size="20">mdi-cog-outline</v-icon>
							</template>
							<v-list-item-title>Settings</v-list-item-title>
						</v-list-item>
						<v-list-item @click="$emit('logout')" class="logout-item">
							<template v-slot:prepend>
								<v-icon size="20">mdi-logout</v-icon>
							</template>
							<v-list-item-title>Log out</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</div>
		<div class="sidebar-overlay" :class="{ 'open': open }" @click="$emit('close')"></div>

		<!-- Rename Dialog -->
		<MGDialog v-model="showRenameDialog" title="Rename Chat" maxWidth="400px" contentHeight="auto">
			<template #content>
				<v-text-field
					v-model="renameValue"
					label="Chat name"
					variant="outlined"
					density="comfortable"
					hide-details
					@keyup.enter="confirmRename"
				></v-text-field>
			</template>
			<template #actions>
				<v-btn class="rename-btn" block @click="confirmRename">Rename</v-btn>
				<v-btn class="cancel-btn" variant="text" block @click="showRenameDialog = false">Cancel</v-btn>
			</template>
		</MGDialog>

	</div>
</template>

<script>
import MGDialog from '@/components/elements/MGDialog.vue';

export default {
	name: "DesktopSidebar",
	components: { MGDialog },
	props: {
		open: { type: Boolean, default: false },
		collapsed: { type: Boolean, default: false },
		colourMode: { type: String, default: 'dark' },
		safeTop: { type: Number, default: 0 },
		userName: { type: String, default: 'User' },
		navItems: { type: Array, default: () => [] },
		pageItems: { type: Array, default: () => [] },
		pageItemsTitle: { type: String, default: 'Recents' },
		pageItemsEmptyText: { type: String, default: '' },
		currentPageItemId: { type: [Number, String], default: null },
		actionButton: { type: Object, default: null }
	},
	emits: ['action-click', 'page-item-click', 'page-item-delete', 'page-item-rename', 'close', 'logout', 'open-settings', 'toggle-collapse'],
	data() {
		return {
			showRenameDialog: false,
			renameValue: '',
			renamingChatId: null,
			navCollapsed: false,
			pageCollapsed: false
		}
	},
	computed: {
		userInitial() {
			return this.userName ? this.userName.charAt(0).toUpperCase() : 'U';
		}
	},
	methods: {
		openRenameDialog(chat) {
			this.renamingChatId = chat.id;
			this.renameValue = chat.title;
			this.showRenameDialog = true;
		},
		confirmRename() {
			if (this.renameValue.trim()) {
				this.$emit('page-item-rename', { id: this.renamingChatId, title: this.renameValue.trim() });
			}
			this.showRenameDialog = false;
		},
		openSettings() {
			this.$emit('open-settings');
		},
		navigateTo(route) {
			this.$emit('close');
			this.$router.push(route);
		},
		isActiveRoute(route) {
			const currentPath = this.$route.path;
			if (route === '/desktop') {
				return currentPath === '/desktop' || currentPath === '/desktop/';
			}
			return currentPath.startsWith(route);
		}
	}
}
</script>

<style scoped>
.sidebar {
	width: 260px;
	background: #252525;
	display: flex;
	flex-direction: column;
	position: fixed;
	left: -260px;
	top: 0;
	bottom: 0;
	z-index: 200;
	transition: left 0.3s ease, width 0.25s ease;
}

.sidebar.open {
	left: 0;
}

.sidebar-overlay {
	display: none;
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: 199;
}

.sidebar-overlay.open {
	display: block;
}

.sidebar-header {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 16px 20px;
	border-bottom: 1px solid #3a3a3a;
	transition: padding 0.25s ease, gap 0.25s ease;
}

.sidebar-logo {
	width: 28px;
	height: 28px;
	border-radius: 50%;
}

.sidebar-title {
	color: #e8e4df;
	font-size: 16px;
	white-space: nowrap;
	overflow: hidden;
	transition: opacity 0.2s ease, width 0.25s ease;
}

.sidebar-menu {
	padding: 12px;
}

.sidebar-item {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 10px 12px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: #e8e4df;
	font-size: 14px;
	cursor: pointer;
	text-align: left;
	transition: background 0.15s, padding 0.25s ease, gap 0.25s ease;
}

.sidebar-item span {
	white-space: nowrap;
	overflow: hidden;
	transition: opacity 0.2s ease, width 0.25s ease;
}

.sidebar-item:hover {
	background: #3a3a3a;
}

.sidebar-item.active {
	background: #3a3a3a;
}

.sidebar-item.new-chat {
	border: 1px solid #444;
	transition: background 0.15s, opacity 0.2s ease, height 0.25s ease, padding 0.25s ease;
}

.sidebar-item .v-icon {
	color: #888;
}

.sidebar-section {
	flex: 1;
	overflow-y: auto;
	padding: 0 12px;
}

.sidebar-spacer {
	flex: 1;
}

.sidebar-section-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 12px 12px 8px 12px;
	background: transparent;
	border: none;
	color: #888;
	font-size: 12px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	white-space: nowrap;
	overflow: hidden;
	cursor: pointer;
	transition: opacity 0.2s ease, height 0.25s ease, padding 0.25s ease, color 0.15s;
}

.sidebar-section-title:hover {
	color: #aaa;
}

.sidebar-section-title .v-icon {
	color: inherit;
}

/* Section collapse animation */
.section-content {
	overflow: hidden;
}

.section-collapse-enter-active,
.section-collapse-leave-active {
	transition: all 0.25s ease;
	overflow: hidden;
}

.section-collapse-enter-from,
.section-collapse-leave-to {
	opacity: 0;
	max-height: 0 !important;
}

.section-collapse-enter-to,
.section-collapse-leave-from {
	opacity: 1;
	max-height: 500px;
}

.sidebar-nav {
	flex: none;
	border-bottom: 1px solid #3a3a3a;
	padding-bottom: 8px;
	overflow-y: visible;
}

.chat-item-wrapper {
	display: flex;
	align-items: center;
	padding: 6px 8px 6px 12px;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.15s, opacity 0.2s ease, height 0.25s ease, padding 0.25s ease;
}

.chat-item-wrapper:hover {
	background: #3a3a3a;
}

.chat-item-wrapper.active {
	background: #3a3a3a;
}

.chat-item-content {
	flex: 1;
	overflow: hidden;
	color: #e8e4df;
	font-size: 14px;
	padding: 4px 0;
}

.item-title {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.rich-item {
	align-items: flex-start;
	padding: 10px 12px;
	border-bottom: 1px solid #3a3a3a;
	border-radius: 0;
}

.rich-item:last-child {
	border-bottom: none;
}

.rich-item .chat-item-content {
	padding: 0;
}

.rich-item .item-title {
	font-size: 13px;
	margin-bottom: 2px;
}

.item-subtitle {
	font-size: 12px;
	color: #888;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.item-meta {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-top: 4px;
	font-size: 11px;
	color: #888;
}

.item-meta .v-chip {
	height: 18px !important;
	font-size: 10px !important;
}

.divider-item {
	cursor: default !important;
	pointer-events: none;
}

.divider-item:hover {
	background: transparent !important;
}

.divider-item .item-title {
	text-align: center;
	font-size: 11px;
	color: #888;
	letter-spacing: 0.5px;
}

.item-progress {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
}

.progress-bar {
	flex: 1;
	height: 6px;
	background: #3a3a3a;
	border-radius: 3px;
	overflow: hidden;
}

.sidebar.light .progress-bar {
	background: var(--border-color);
}

.progress-fill {
	height: 100%;
	border-radius: 3px;
	transition: width 0.3s ease;
}

.progress-text {
	font-size: 11px;
	min-width: 32px;
	text-align: right;
}

.chat-menu-btn {
	opacity: 0;
	background: transparent;
	border: none;
	padding: 4px;
	border-radius: 4px;
	color: #888;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: opacity 0.15s, background 0.15s;
}

.chat-item-wrapper:hover .chat-menu-btn {
	opacity: 1;
}

.chat-menu-btn:hover {
	background: #4a4a4a;
	color: #e8e4df;
}

.sidebar-empty {
	padding: 12px;
	color: #666;
	font-size: 13px;
}

/* Menu list styling */
.chat-menu-list {
	background: #3a3a3a !important;
	border-radius: 12px !important;
	padding: 4px !important;
	min-width: 140px;
}

.chat-menu-list :deep(.v-list-item) {
	border-radius: 8px;
	min-height: 40px;
	color: #e8e4df;
}

.chat-menu-list :deep(.v-list-item:hover) {
	background: #4a4a4a;
}

.chat-menu-list :deep(.v-list-item-title) {
	font-size: 14px;
	color: #e8e4df;
}

.delete-text {
	color: #e57373 !important;
}

.rename-btn {
	background: #c4967a !important;
	color: white !important;
	text-transform: none !important;
	margin-bottom: 8px;
}

.cancel-btn {
	color: #888 !important;
	text-transform: none !important;
}

/* Account footer */
.sidebar-footer {
	padding: 12px;
	border-top: 1px solid #3a3a3a;
}

.account-btn {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 8px 10px;
	background: transparent;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	transition: background-color 0.15s, padding 0.25s ease, gap 0.25s ease;
}

.account-btn:hover {
	background: #3a3a3a;
}

.account-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: #555;
	color: #e8e4df;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
}

.account-info {
	flex: 1;
	text-align: left;
	overflow: hidden;
	transition: opacity 0.2s ease, width 0.25s ease;
}

.account-name {
	color: #e8e4df;
	font-size: 14px;
}

.account-chevron {
	color: #888;
	transition: opacity 0.2s ease;
}

/* Account menu */
.account-menu-list {
	background: #3a3a3a !important;
	border-radius: 12px !important;
	padding: 4px !important;
	min-width: 180px;
}

.account-menu-list :deep(.v-list-item) {
	border-radius: 8px;
	min-height: 44px;
	color: #e8e4df;
}

.account-menu-list :deep(.v-list-item:hover) {
	background: #4a4a4a;
}

.account-menu-list :deep(.v-list-item-title) {
	font-size: 14px;
	color: #e8e4df;
}

.account-menu-list :deep(.v-icon) {
	color: #888;
}

/* Light Mode */
.sidebar.light {
	background: #f0ede8;
}

.sidebar.light .sidebar-header {
	border-color: var(--border-color);
}

.sidebar.light .sidebar-title {
	color: var(--text-color);
}

.sidebar.light .sidebar-item {
	color: var(--text-color);
}

.sidebar.light .sidebar-item:hover {
	background: var(--background-color-alt);
}

.sidebar.light .sidebar-item.active {
	background: var(--border-color) !important;
}

.sidebar.light .sidebar-item.new-chat {
	border-color: var(--border-color);
}

.sidebar.light .sidebar-item .v-icon {
	color: var(--grey-text);
}

.sidebar.light .sidebar-section-title {
	color: var(--grey-text);
}

.sidebar.light .sidebar-section-title:hover {
	color: var(--text-color);
}

.sidebar.light .chat-item-wrapper:hover {
	background: var(--background-color-alt) !important;
}

.sidebar.light .chat-item-wrapper.active {
	background: var(--border-color) !important;
}

.sidebar.light .chat-item-content {
	color: var(--text-color);
}

.sidebar.light .rich-item {
	border-color: var(--border-color);
}

.sidebar.light .item-subtitle,
.sidebar.light .item-meta {
	color: var(--grey-text);
}

.sidebar.light .chat-menu-btn {
	color: var(--grey-text);
}

.sidebar.light .chat-menu-btn:hover {
	background: var(--border-color);
	color: var(--text-color);
}

.sidebar.light .sidebar-empty {
	color: var(--grey-text);
}

.sidebar.light .sidebar-nav {
	border-color: var(--border-color);
}

.sidebar.light .sidebar-footer {
	border-color: var(--border-color);
}

.sidebar.light .account-btn:hover {
	background: var(--background-color-alt);
}

.sidebar.light .account-avatar {
	background: var(--border-color);
	color: var(--text-color);
}

.sidebar.light .account-name {
	color: var(--text-color);
}

.sidebar.light .account-chevron {
	color: var(--grey-text);
}

/* Collapse toggle button */
.collapse-btn {
	margin-left: auto;
	background: transparent;
	border: none;
	padding: 4px;
	border-radius: 4px;
	color: #888;
	cursor: pointer;
	display: none;
	align-items: center;
	justify-content: center;
	transition: background 0.15s, color 0.15s;
}

.collapse-btn:hover {
	background: #3a3a3a;
	color: #e8e4df;
}

.sidebar.light .collapse-btn:hover {
	background: var(--background-color-alt);
	color: var(--text-color);
}

/* Collapsed state */
.sidebar.collapsed {
	width: 64px;
}

.sidebar.collapsed .sidebar-header {
	justify-content: center;
	padding: 16px 12px;
	gap: 0;
}

.sidebar.collapsed .sidebar-title {
	opacity: 0;
	width: 0;
}

.sidebar.collapsed .collapse-btn {
	margin-left: 0;
}

.sidebar.collapsed .sidebar-section-title {
	opacity: 0;
	height: 0;
	padding: 0 12px;
}

.sidebar.collapsed .sidebar-item span {
	opacity: 0;
	width: 0;
}

.sidebar.collapsed .sidebar-item {
	justify-content: center;
	padding: 10px;
	gap: 0;
}

.sidebar.collapsed .sidebar-item.new-chat {
	opacity: 0;
	height: 0;
	padding: 0;
	border: none;
	overflow: hidden;
}

.sidebar.collapsed .chat-item-wrapper {
	opacity: 0;
	height: 0;
	padding: 0;
	overflow: hidden;
}

.sidebar.collapsed .sidebar-empty {
	opacity: 0;
	height: 0;
	overflow: hidden;
}

.sidebar.collapsed .account-btn {
	justify-content: center;
	padding: 8px;
	gap: 0;
}

.sidebar.collapsed .account-info {
	opacity: 0;
	width: 0;
}

.sidebar.collapsed .account-chevron {
	opacity: 0;
	width: 0;
}

/* Desktop: show sidebar always */
@media (min-width: 1024px) {
	.sidebar {
		left: 0;
	}

	.sidebar.collapsed {
		left: 0;
	}

	.collapse-btn {
		display: flex;
	}

	.sidebar-overlay {
		display: none !important;
	}
}
</style>

<style>
/* Dark rename dialog - only when desktop is in dark mode */
body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card {
	background: #3a3a3a !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-card-title {
	background: #3a3a3a !important;
	color: #e8e4df !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-card-text {
	background: #3a3a3a !important;
	color: #e8e4df !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-card-actions {
	background: #3a3a3a !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-field {
	background: #2f2f2f !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-field__outline {
	--v-field-border-color: #555 !important;
	color: #555 !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-field--focused .v-field__outline {
	--v-field-border-color: #888 !important;
	color: #888 !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-field input {
	color: #e8e4df !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .v-label {
	color: #888 !important;
}

body:has(.desktop-layout.dark) .v-dialog .mg-dialog-card .title {
	color: #e8e4df !important;
}
</style>
